/**
 * FeedbackWidget — Firebase-native replacement for Userback.
 *
 * Renders a bottom-left "bug ribbon" trigger (🐞). On click (or
 * `open-feedback-widget` event), captures a screenshot via html2canvas, shows a
 * modal with preview + comment textarea, then uploads to Firebase Storage and
 * writes metadata to Firestore `feedback_reports` collection.
 *
 * Zero external SaaS dependencies.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../lib/firebase';

declare global {
  interface Window {
    html2canvas?: (element: HTMLElement, options?: object) => Promise<HTMLCanvasElement>;
  }
}

const UPLOAD_TIMEOUT_MS = 12_000;
type SubmitStage = 'idle' | 'uploading' | 'writing' | 'success' | 'error';

/* ------------------------------------------------------------------ */
/*  html2canvas is loaded lazily from CDN (MVP-friendly).              */
/* ------------------------------------------------------------------ */
const ensureHtml2CanvasLoaded = async () => {
  if (typeof window === 'undefined') return;
  if (window.html2canvas) return;

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-html2canvas="1"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load html2canvas')));
      return;
    }

    const s = document.createElement('script');
    s.async = true;
    s.dataset.html2canvas = '1';
    // Pin exact version for deterministic behavior.
    s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load html2canvas'));
    (document.head || document.body).appendChild(s);
  });
};

/* ------------------------------------------------------------------ */
/*  Metadata helpers                                                   */
/* ------------------------------------------------------------------ */
const getBrowserMeta = () => {
  const ua = navigator.userAgent;
  const match = ua.match(/(Chrome|Firefox|Safari|Edge|OPR)\/(\d+)/);
  const browser = match ? `${match[1]} ${match[2]}` : ua.slice(0, 60);
  const os = navigator.platform || 'unknown';
  const viewport = `${window.innerWidth}x${window.innerHeight}`;
  return { browser, os, viewport };
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const FeedbackWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<SubmitStage>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Listen for custom event dispatched by captive portal "Feedback" link
  useEffect(() => {
    const handler = () => capture();
    window.addEventListener('open-feedback-widget', handler);
    return () => window.removeEventListener('open-feedback-widget', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------- capture -------- */
  const capture = useCallback(async () => {
    try {
      await ensureHtml2CanvasLoaded();
      if (!window.html2canvas) throw new Error('html2canvas not available');

      const canvas = await window.html2canvas(document.body, {
        useCORS: true,
        scale: 0.5,
        logging: false,
        ignoreElements: (el: Element) => el.id === 'feedback-widget-root',
      });
      canvas.toBlob((blob: Blob | null) => {
        if (!blob) return;
        setScreenshotBlob(blob);
        setScreenshotUrl(URL.createObjectURL(blob));
        setIsOpen(true);
        setTimeout(() => textareaRef.current?.focus(), 120);
      }, 'image/png');
    } catch (e) {
      console.warn('[FeedbackWidget] capture failed:', e);
      setIsOpen(true);
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, []);

  /* -------- submit -------- */
  const handleSubmit = async () => {
    if (stage !== 'idle' && stage !== 'error') return;
    setStage('uploading');
    setSubmitError(null);
    let screenshotStatus = 'none';

    try {
      let downloadURL = '';

      if (screenshotBlob && storage) {
        try {
          const filename = `feedback/${Date.now()}.png`;
          const storageRef = ref(storage, filename);
          const uploadTimeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('upload timeout')), UPLOAD_TIMEOUT_MS)
          );
          await Promise.race([uploadBytes(storageRef, screenshotBlob), uploadTimeout]);
          downloadURL = await getDownloadURL(storageRef);
          screenshotStatus = 'ok';
        } catch {
          screenshotStatus = 'skipped';
        }
      }

      setStage('writing');
      if (db) {
        await addDoc(collection(db, 'feedback_reports'), {
          userId: auth?.currentUser?.uid ?? 'anonymous',
          userEmail: auth?.currentUser?.email ?? '',
          screenshotUrl: downloadURL,
          comment: comment.trim(),
          url: window.location.href,
          metadata: getBrowserMeta(),
          status: 'new',
          timestamp: serverTimestamp(),
          screenshot_upload_status: screenshotStatus,
          submit_stage_last: 'writing',
        });
      }

      setStage('success');
      setTimeout(() => close(), 1800);
    } catch (e) {
      console.error('[FeedbackWidget] submit failed:', e);
      setSubmitError('Feedback submission failed — please try again.');
      setStage('error');
    }
  };

  /* -------- close / reset -------- */
  const close = () => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setIsOpen(false);
    setScreenshotUrl(null);
    setScreenshotBlob(null);
    setComment('');
    setStage('idle');
    setSubmitError(null);
  };

  return (
    <div id="feedback-widget-root">
      {/* Bug ribbon trigger */}
      <button
        data-testid="feedback-fab"
        onClick={capture}
        className="group fixed bottom-5 left-0 z-[9999] focus-visible:outline-none"
        title="Report a bug"
        aria-label="Report a bug"
      >
        <span className="block bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg border border-white/10 rounded-r-full skew-x-[-12deg] overflow-hidden w-10 group-hover:w-36 group-focus-visible:w-36 transition-all duration-200 ease-out">
          <span className="inline-flex items-center gap-2 text-sm font-semibold skew-x-[12deg] px-2.5 py-2 whitespace-nowrap">
            <span aria-hidden className="text-base leading-none">🐞</span>
            <span className="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200">
              Report bug
            </span>
          </span>
        </span>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          data-testid="feedback-modal"
        >
          <div className="w-full max-w-md mx-4 bg-[#0d1117] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
              <h3 className="text-sm font-bold text-white">
                {stage === 'success' ? 'Thanks!' : 'Send Feedback'}
              </h3>
              <button
                onClick={close}
                className="text-slate-400 hover:text-white text-lg leading-none"
                aria-label="Close feedback"
              >
                ✕
              </button>
            </div>

            {stage === 'success' ? (
              <div className="px-5 py-8 text-center">
                <span className="text-4xl">🎉</span>
                <p className="mt-3 text-sm text-slate-300">Feedback received — thank you!</p>
              </div>
            ) : (
              <div className="px-5 py-4 space-y-4">
                {screenshotUrl && (
                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <img src={screenshotUrl} alt="Page screenshot preview" className="w-full object-cover max-h-36" />
                  </div>
                )}

                <textarea
                  ref={textareaRef}
                  data-testid="feedback-comment"
                  placeholder="What's wrong? What could be better?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 resize-none"
                />

                {submitError && (
                  <p className="text-xs text-red-400">{submitError}</p>
                )}

                <button
                  data-testid="feedback-submit"
                  onClick={handleSubmit}
                  disabled={!['idle', 'error'].includes(stage) || !comment.trim()}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {stage === 'uploading' ? 'Uploading…' : stage === 'writing' ? 'Saving…' : stage === 'error' ? 'Retry' : 'Send'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
